import React, { useState } from 'react';
import { analysisApi, promptApi } from '../services/api';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Tabs } from './ui/tabs';
import { Button } from './ui/button';
import SearchInput from './ui/SearchInput';

interface UrlResult {
  promptId: string;
  promptName?: string;
  isBrandSafe: boolean;
  confidence: number;
  reasoning: string;
  contentSummary: string;
  analyzedAt: Date;
  provider: string;
  model: string;
}

type EndpointType = 'detailed' | 'simplified';
type GamTagType = 'static' | 'dynamic' | 'minimal';

const MODE_TABS = [
  { value: 'detailed', label: 'Detailed' },
  { value: 'simplified', label: 'Simplified' },
];

const TAG_TYPE_TABS = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
  { value: 'minimal', label: 'Minimal' },
];

const microlabel = 'block text-xs font-bold uppercase tracking-wide text-ink-muted';
const insetLabel = 'mb-1 block text-xs font-bold uppercase tracking-wide text-ink';

const UrlInformation: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<UrlResult[]>([]);
  const [searchedUrl, setSearchedUrl] = useState<string | null>(null);
  const [endpointType, setEndpointType] = useState<EndpointType>('detailed');
  const [simplifiedResults, setSimplifiedResults] = useState<{
    brand_safety: string[];
    audiences: string[];
    scan_status: {
      has_been_scanned: boolean;
      scanned_brand_safety_prompts: string[];
      scanned_audience_prompts: string[];
      total_prompts_scanned: number;
    };
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [gamTagType, setGamTagType] = useState<GamTagType>('static');
  const [showRawJson, setShowRawJson] = useState(false);

  const handleSearch = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setSimplifiedResults(null);
    setSearchedUrl(null);
    setShowRawJson(false);

    try {
      if (endpointType === 'detailed') {
        // Fetch all prompts to get their names
        const promptsData = await promptApi.getAll();
        const promptMap = new Map(promptsData.map(p => [p.id, p.name]));

        // Fetch URL information
        const response = await analysisApi.getUrlInformation(url);

        if (response.success && response.results.length > 0) {
          // Enrich results with prompt names
          const enrichedResults = response.results.map(result => ({
            ...result,
            promptName: promptMap.get(result.promptId) || 'Unknown Prompt'
          }));
          setResults(enrichedResults);
          setSearchedUrl(response.url);
        } else {
          setError('No analysis results found for this URL');
        }
      } else if (endpointType === 'simplified') {
        // Fetch simplified matches
        const response = await analysisApi.getUrlMatches(url);
        setSimplifiedResults(response);
        setSearchedUrl(url);
      }
    } catch (err) {
      console.error('Failed to fetch URL information:', err);
      setError('Failed to fetch URL information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-safe';
    if (confidence >= 0.6) return 'text-warn';
    return 'text-risk';
  };

  const copyGAMTag = async () => {
    if (!simplifiedResults) return;

    const gamTag = `<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" crossorigin="anonymous"></script>
<script>
  window.googletag = window.googletag || {cmd: []};

  googletag.cmd.push(function() {
    googletag.defineSlot('/23206864062/test-eskimi/eskimi-creatives.com/eskimi-creatives.com_display_300x250', [300, 250], 'div-gpt-ad-1752131461665-0').addService(googletag.pubads());

    googletag.pubads().enableSingleRequest();

    // Set the key with an array of string values
    googletag.pubads().setTargeting('deepcontext_brandsafety', ${JSON.stringify(simplifiedResults.brand_safety)});
    googletag.pubads().setTargeting('deepcontext_audiences', ${JSON.stringify(simplifiedResults.audiences)});

    googletag.enableServices();
  });
</script>`;

    try {
      await navigator.clipboard.writeText(gamTag);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy GAM tag:', err);
    }
  };

  const copyDynamicGAMTag = async () => {
    // Get the current API base URL from the app
    const currentApiBase = window.location.origin;

    const dynamicGamTag = `<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" crossorigin="anonymous"></script>
<script>
  window.googletag = window.googletag || {cmd: []};

  // DeepContext API Configuration
  const DEEPCONTEXT_API_BASE = '${currentApiBase}';
  const CURRENT_URL = window.location.href;

  // Function to fetch targeting data from DeepContext API
  async function fetchDeepContextTargeting() {
    try {
      const encodedUrl = encodeURIComponent(CURRENT_URL);
      const response = await fetch(\`\${DEEPCONTEXT_API_BASE}/api/analysis/url/\${encodedUrl}/matches\`);

      if (!response.ok) {
        console.warn('DeepContext API request failed:', response.status);
        return { brand_safety: [], audiences: [] };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.warn('DeepContext API error:', error);
      return { brand_safety: [], audiences: [] };
    }
  }

  // Initialize GAM with DeepContext targeting
  async function initializeGAMWithDeepContext() {
    const targeting = await fetchDeepContextTargeting();

    googletag.cmd.push(function() {
      googletag.defineSlot('/23206864062/test-eskimi/eskimi-creatives.com/eskimi-creatives.com_display_300x250', [300, 250], 'div-gpt-ad-1752131461665-0').addService(googletag.pubads());

      googletag.pubads().enableSingleRequest();

      // Apply DeepContext targeting
      if (targeting.brand_safety && targeting.brand_safety.length > 0) {
        googletag.pubads().setTargeting('deepcontext_brandsafety', targeting.brand_safety);
      }

      if (targeting.audiences && targeting.audiences.length > 0) {
        googletag.pubads().setTargeting('deepcontext_audiences', targeting.audiences);
      }

      // Optional: Log targeting for debugging
      console.log('DeepContext Targeting Applied:', targeting);

      googletag.enableServices();
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGAMWithDeepContext);
  } else {
    initializeGAMWithDeepContext();
  }
</script>`;

    try {
      await navigator.clipboard.writeText(dynamicGamTag);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy dynamic GAM tag:', err);
    }
  };

  const copyMinimalScript = async () => {
    // Get the current API base URL from the app
    const currentApiBase = window.location.origin;

    const minimalScript = `<script>
(function(){
const DEEPCONTEXT_API='${currentApiBase}';
fetch(DEEPCONTEXT_API+'/api/analysis/url/'+encodeURIComponent(location.href)+'/matches')
.then(r=>r.ok?r.json():{brand_safety:[],audiences:[]})
.then(d=>{if(typeof googletag!=='undefined')googletag.cmd.push(()=>{
if(d.brand_safety?.length)googletag.pubads().setTargeting('deepcontext_brandsafety',d.brand_safety);
if(d.audiences?.length)googletag.pubads().setTargeting('deepcontext_audiences',d.audiences);
})}).catch(()=>{});
})();
</script>`;

    try {
      await navigator.clipboard.writeText(minimalScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy minimal script:', err);
    }
  };

  const cleanReasoning = (reasoning: string) => {
    if (reasoning.startsWith('Parsing error:')) {
      const cleanError = reasoning.split('. Raw response:')[0];
      return cleanError || 'AI response parsing failed. Please try analyzing this URL again.';
    }
    return reasoning;
  };

  return (
    <div className="space-y-4">
      {/* Search row: Detailed/Simplified segmented switch + pill URL input + gradient Search.
          -mt-[21px] pulls the row's centerline up to match the sidebar's
          Explore nav pill centerline (deepcontext-jlkv mechanism,
          deepcontext-fbj1.1 — mirrors SearchFilterBar.tsx's Blueprints
          toolbar). */}
      <div className="-mt-[21px] flex flex-wrap items-center gap-2.5">
        <SearchInput
          className="flex-1"
          placeholder="Enter URL to search…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
        />
        <Tabs
          tabs={MODE_TABS}
          value={endpointType}
          onChange={(value) => setEndpointType(value as EndpointType)}
          className="flex-shrink-0"
        />
        <Button
          onClick={handleSearch}
          disabled={loading || !url.trim()}
          className="flex-shrink-0"
        >
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {error && (
        <div
          data-testid="url-lookup-error-banner"
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-risk bg-risk-bg px-4 py-3"
        >
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-risk" />
          <span className="text-sm text-risk">{error}</span>
        </div>
      )}

      {/* Detailed mode: result cards */}
      {searchedUrl && results.length > 0 && (
        <div className="space-y-3">
          {results.map((result, index) => (
            <div
              key={`${result.promptId}-${index}`}
              data-testid="url-lookup-result-card"
              className="rounded-2xl border border-line bg-surface p-4 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-extrabold text-ink">{result.promptName}</span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${
                    result.isBrandSafe ? 'bg-safe-bg text-safe' : 'bg-risk-bg text-risk'
                  }`}
                >
                  {result.isBrandSafe ? 'Match' : 'No Match'}
                </span>
                <span className="ml-auto text-xs text-ink-muted">
                  {index + 1} of {results.length} result{results.length !== 1 ? 's' : ''} across{' '}
                  {results.length} blueprint{results.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg bg-canvas px-2.5 py-1.5">
                  <span className={microlabel}>Confidence</span>
                  <span className={`text-sm font-extrabold ${getConfidenceColor(result.confidence)}`}>
                    {(result.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="rounded-lg bg-canvas px-2.5 py-1.5">
                  <span className={microlabel}>Provider</span>
                  <span className="text-sm font-extrabold text-ink">{result.provider}</span>
                </div>
                <div className="rounded-lg bg-canvas px-2.5 py-1.5">
                  <span className={microlabel}>Model</span>
                  <span className="truncate text-sm font-extrabold text-ink" title={result.model}>
                    {result.model}
                  </span>
                </div>
                <div className="rounded-lg bg-canvas px-2.5 py-1.5">
                  <span className={microlabel}>Analyzed</span>
                  <span className="text-sm font-extrabold text-ink">{formatDate(result.analyzedAt)}</span>
                </div>
              </div>

              {result.contentSummary && (
                <div className="mt-2 rounded-xl bg-canvas p-3 text-sm leading-relaxed text-ink-muted">
                  <span className={insetLabel}>Content Summary</span>
                  {result.contentSummary}
                </div>
              )}

              <div className="mt-2 rounded-xl bg-canvas p-3 text-sm leading-relaxed text-ink-muted">
                <span className={insetLabel}>Analysis Reasoning</span>
                {cleanReasoning(result.reasoning)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Simplified mode: GAM tag card */}
      {searchedUrl && simplifiedResults && endpointType === 'simplified' && (
        <div data-testid="url-lookup-gam-card" className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[13px] font-extrabold text-ink">GAM Tag with Targeting Values</span>
            <Tabs
              tabs={TAG_TYPE_TABS}
              value={gamTagType}
              onChange={(value) => setGamTagType(value as GamTagType)}
            />
            <Button
              variant="outline"
              size="sm"
              className={`ml-auto ${copied ? 'border-safe bg-safe-bg text-safe hover:bg-safe-bg' : ''}`}
              onClick={() => {
                if (gamTagType === 'static') copyGAMTag();
                else if (gamTagType === 'dynamic') copyDynamicGAMTag();
                else copyMinimalScript();
              }}
            >
              <ClipboardIcon className="h-3.5 w-3.5" />
              {copied ? 'Copied!' : 'Copy Tag'}
            </Button>
          </div>

          <div
            data-testid="url-lookup-scan-status"
            className="mt-3 rounded-xl bg-canvas p-3 text-sm text-ink-muted"
          >
            {simplifiedResults.scan_status.has_been_scanned ? (
              <>
                <div className="flex items-center gap-1.5 font-semibold text-safe">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  This URL has been scanned
                </div>
                <div className="mt-1">
                  Brand Safety Prompts: {simplifiedResults.scan_status.scanned_brand_safety_prompts.length} ·{' '}
                  Audience Prompts: {simplifiedResults.scan_status.scanned_audience_prompts.length} · Total
                  Scanned: {simplifiedResults.scan_status.total_prompts_scanned}
                  <br />
                  Current Matches — Brand Safety: {simplifiedResults.brand_safety.length} / Audiences:{' '}
                  {simplifiedResults.audiences.length}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-1.5 font-semibold text-warn">
                <XCircleIcon className="h-3.5 w-3.5" />
                This URL has not been scanned yet
              </div>
            )}
          </div>

          <div
            data-testid="url-lookup-code-block"
            className="mt-2 overflow-x-auto rounded-xl bg-[#17181c] p-3 font-mono text-xs leading-relaxed text-success-500"
          >
            <pre className="whitespace-pre-wrap">
{gamTagType === 'static' ?
`<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" crossorigin="anonymous"></script>
<script>
  window.googletag = window.googletag || {cmd: []};

  googletag.cmd.push(function() {
    googletag.defineSlot('/23206864062/test-eskimi/eskimi-creatives.com/eskimi-creatives.com_display_300x250', [300, 250], 'div-gpt-ad-1752131461665-0').addService(googletag.pubads());

    googletag.pubads().enableSingleRequest();

    // Set the key with an array of string values
    googletag.pubads().setTargeting('deepcontext_brandsafety', ${JSON.stringify(simplifiedResults.brand_safety)});
    googletag.pubads().setTargeting('deepcontext_audiences', ${JSON.stringify(simplifiedResults.audiences)});

    googletag.enableServices();
  });
</script>` : gamTagType === 'dynamic' ?
`<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" crossorigin="anonymous"></script>
<script>
  window.googletag = window.googletag || {cmd: []};

  // DeepContext API Configuration
  const DEEPCONTEXT_API_BASE = '${window.location.origin}';
  const CURRENT_URL = window.location.href;

  // Function to fetch targeting data from DeepContext API
  async function fetchDeepContextTargeting() {
    try {
      const encodedUrl = encodeURIComponent(CURRENT_URL);
      const response = await fetch(\`\${DEEPCONTEXT_API_BASE}/api/analysis/url/\${encodedUrl}/matches\`);

      if (!response.ok) {
        console.warn('DeepContext API request failed:', response.status);
        return { brand_safety: [], audiences: [] };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.warn('DeepContext API error:', error);
      return { brand_safety: [], audiences: [] };
    }
  }

  // Initialize GAM with DeepContext targeting
  async function initializeGAMWithDeepContext() {
    const targeting = await fetchDeepContextTargeting();

    googletag.cmd.push(function() {
      googletag.defineSlot('/23206864062/test-eskimi/eskimi-creatives.com/eskimi-creatives.com_display_300x250', [300, 250], 'div-gpt-ad-1752131461665-0').addService(googletag.pubads());

      googletag.pubads().enableSingleRequest();

      // Apply DeepContext targeting
      if (targeting.brand_safety && targeting.brand_safety.length > 0) {
        googletag.pubads().setTargeting('deepcontext_brandsafety', targeting.brand_safety);
      }

      if (targeting.audiences && targeting.audiences.length > 0) {
        googletag.pubads().setTargeting('deepcontext_audiences', targeting.audiences);
      }

      // Optional: Log targeting for debugging
      console.log('DeepContext Targeting Applied:', targeting);

      googletag.enableServices();
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGAMWithDeepContext);
  } else {
    initializeGAMWithDeepContext();
  }
</script>` :
`<script>
(function(){
const DEEPCONTEXT_API='${window.location.origin}';
fetch(DEEPCONTEXT_API+'/api/analysis/url/'+encodeURIComponent(location.href)+'/matches')
.then(r=>r.ok?r.json():{brand_safety:[],audiences:[]})
.then(d=>{if(typeof googletag!=='undefined')googletag.cmd.push(()=>{
if(d.brand_safety?.length)googletag.pubads().setTargeting('deepcontext_brandsafety',d.brand_safety);
if(d.audiences?.length)googletag.pubads().setTargeting('deepcontext_audiences',d.audiences);
})}).catch(()=>{});
})();
</script>`}
            </pre>
          </div>

          {gamTagType === 'static' && (
            <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div className="rounded-xl bg-nav-active p-3 text-sm text-ink">
                <span className={insetLabel}>Brand Safety Targeting</span>
                <p className="text-ink-muted">
                  {simplifiedResults.brand_safety.length} prompt{simplifiedResults.brand_safety.length !== 1 ? 's' : ''} matched
                </p>
                <div className="mt-1 rounded-lg border border-line bg-surface p-1.5 font-mono text-xs">
                  deepcontext_brandsafety: {JSON.stringify(simplifiedResults.brand_safety)}
                </div>
              </div>
              <div className="rounded-xl bg-ghost-bg p-3 text-sm text-ink">
                <span className={insetLabel}>Audience Targeting</span>
                <p className="text-ink-muted">
                  {simplifiedResults.audiences.length} prompt{simplifiedResults.audiences.length !== 1 ? 's' : ''} matched
                </p>
                <div className="mt-1 rounded-lg border border-line bg-surface p-1.5 font-mono text-xs">
                  deepcontext_audiences: {JSON.stringify(simplifiedResults.audiences)}
                </div>
              </div>
            </div>
          )}

          {gamTagType === 'dynamic' && (
            <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div className="rounded-xl bg-warn-bg p-3 text-sm text-warn">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide">Dynamic Tag Features</span>
                <ul className="space-y-1">
                  <li>Detects the current page URL via <code className="rounded bg-surface px-1">window.location.href</code></li>
                  <li>Calls your DeepContext API endpoint for real-time targeting data</li>
                  <li>Works on any page without hardcoded values</li>
                  <li>Falls back to empty arrays on API failure</li>
                  <li>Remember to update <code className="rounded bg-surface px-1">DEEPCONTEXT_API_BASE</code> with your API domain</li>
                </ul>
              </div>
              <div className="rounded-xl bg-safe-bg p-3 text-sm text-safe">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide">Standalone Library</span>
                <p>For easier implementation, use the standalone JavaScript library instead:</p>
                <div className="mt-1 rounded-lg bg-surface p-1.5 font-mono text-xs text-ink">
                  &lt;script src="deepcontext-gam-integration.js"&gt;&lt;/script&gt;
                </div>
              </div>
            </div>
          )}

          {gamTagType === 'minimal' && (
            <div className="mt-2 space-y-2.5">
              <div className="rounded-xl bg-canvas p-3 text-sm leading-relaxed text-ink-muted">
                <span className={insetLabel}>About This Tag</span>
                Non-invasive and lightweight — doesn't interfere with your existing GAM setup, auto-configured
                to the current domain, and falls back silently if the API call fails. No authentication
                required; CORS is enabled for any domain.
              </div>
              <div className="rounded-xl bg-[#17181c] p-3 text-sm leading-relaxed text-white/80">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-white/60">
                  Integration Steps
                </span>
                <ol className="list-inside list-decimal space-y-1">
                  <li>Copy the script above (API URL is already configured)</li>
                  <li>Paste it in your website's HTML after your existing GAM code</li>
                  <li>Place it anywhere after &lt;script src="gpt.js"&gt;</li>
                  <li>That's it — your GAM ads now receive DeepContext targeting</li>
                </ol>
              </div>
            </div>
          )}

          <div className="mt-3">
            <button
              type="button"
              data-testid="url-lookup-raw-json-toggle"
              onClick={() => setShowRawJson((v) => !v)}
              className="flex w-full items-center justify-between rounded-xl bg-canvas px-3 py-2 text-xs font-bold uppercase tracking-wide text-ink-muted"
            >
              Raw JSON Response
              {showRawJson ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
            </button>
            {showRawJson && (
              <pre
                data-testid="url-lookup-raw-json"
                className="mt-1.5 overflow-x-auto rounded-xl bg-canvas p-3 font-mono text-xs text-ink-muted"
              >
                {JSON.stringify(simplifiedResults, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {searchedUrl && ((endpointType === 'detailed' && results.length === 0) ||
                       (endpointType === 'simplified' && simplifiedResults &&
                        simplifiedResults.brand_safety.length === 0 &&
                        simplifiedResults.audiences.length === 0)) && !loading && !error && (
        <div className="rounded-2xl bg-canvas p-10 text-center">
          <ExclamationCircleIcon className="mx-auto mb-3 h-10 w-10 text-ink-muted" />
          <h3 className="mb-1 text-sm font-bold text-ink">No Results Found</h3>
          <p className="text-xs text-ink-muted">No analysis results were found for the URL you searched.</p>
        </div>
      )}
    </div>
  );
};

export default UrlInformation;
